// React
import { createContext, Dispatch, MutableRefObject, ReactNode, useContext, useMemo, useReducer, useState } from "react";
// MUI
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

// Hooks
import useConfigurationData from "@Common/hooks/Configuration/useConfigurationData";

// Utils
import {
	initialReconciliationOptions,
	ReconciliationExportAction,
	ReconciliationExportOptions,
	reconciliationExportReducer,
	TableFilterAction,
	tableFilterReducer,
	tableFiltersInitialState,
	TableFilterState,
} from "@DataPages/helpers/salesOperations/salesPerformance/crossStoreCommissionReducers";

// Types
import type { ISystemSettings } from "@/Settings/types/Common/model";
import type { IDmsControlType } from "@/Settings/types/administrationTools/model";
import type {
	IExportFilter,
	IGroupInclusion,
	IPositionsExported,
} from "@DataPages/types/salesOperations/salesPerformance/crossStoreReconciliationUtils";
import type { Nullable } from "@Common/interfaces/types";

//Types
import { ISalesPerformanceExportInput } from "@/DataPages/types/salesOperations/salesPerformance/models/crossStoreReconciliation";

interface ICrossStoreReconciliationContext {
	dmsControlTypeList: IDmsControlType[];
	branchIdList: string[];
	branchIdListTransformed: { label: string; value: string }[];
	systemSettings: Nullable<ISystemSettings>;
	tableFilterState: TableFilterState;
	tableFilterDispatch: Dispatch<TableFilterAction>;
	reconciliationExportOptions: ReconciliationExportOptions;
	reconciliationExportDispatch: Dispatch<ReconciliationExportAction>;
	filteredCrossStoreReconciliation: ISalesPerformanceExportInput[];
	setFilteredCrossStoreReconciliation: (value: ISalesPerformanceExportInput[]) => void;
	apiRef: MutableRefObject<GridApiPremium>;
}
const CrossStoreReconciliationContext = createContext<ICrossStoreReconciliationContext>({
	dmsControlTypeList: [],
	branchIdList: [],
	branchIdListTransformed: [],
	systemSettings: null,
	//
	tableFilterState: {
		groupsInclusions: [] as IGroupInclusion[],
		exportFilters: [] as IExportFilter[],
		positionsExported: [] as IPositionsExported[],
	},
	tableFilterDispatch: () => {},
	//
	reconciliationExportOptions: {
		viewMode: "ITEMIZED",
		exportMode: "CSV",
		controlType: undefined,
		branchId: undefined,
		markAsReconciled: false,
	},
	reconciliationExportDispatch: () => {},
	filteredCrossStoreReconciliation: [],
	setFilteredCrossStoreReconciliation: () => {},
	apiRef: { current: {} as GridApiPremium },
});

interface ICrossStoreReconciliationContextProviderProps {
	children: ReactNode;
	locationId: string;
}

const requiredDmsControlTypes = ["Customer Number", "Employee Number", "Stock Number", "Deal Number"];

const CrossStoreReconciliationContextProvider = (props: Readonly<ICrossStoreReconciliationContextProviderProps>) => {
	const { children, locationId } = props;

	const apiRef = useGridApiRef();

	// Reducers
	const [tableFilterState, tableFilterDispatch] = useReducer(tableFilterReducer, tableFiltersInitialState);
	const [reconciliationExportOptions, reconciliationExportDispatch] = useReducer(
		reconciliationExportReducer,
		initialReconciliationOptions
	);

	// States
	const [filteredCrossStoreReconciliation, setFilteredCrossStoreReconciliation] = useState<ISalesPerformanceExportInput[]>([]);

	// Data from configuration
	const { configuration } = useConfigurationData({ locationId });
	const { administrationTools, systemSettings } = configuration ?? {};
	const dmsControlTypeList =
		administrationTools?.integrations?.dmsControlTypes?.filter((controlType) => requiredDmsControlTypes.includes(controlType.label)) ??
		[];
	const branchIdList = configuration?.administrationTools?.integrations?.dmsProvider?.areaNumber ?? [];
	const branchIdListTransformed = branchIdList.map((branch) => ({ label: branch, value: branch }));

	// This is what the Context is providing to be used in the children components
	const value = useMemo(
		() => ({
			apiRef,
			dmsControlTypeList,
			branchIdList,
			branchIdListTransformed,
			systemSettings,
			tableFilterState,
			tableFilterDispatch,
			reconciliationExportOptions,
			reconciliationExportDispatch,
			filteredCrossStoreReconciliation,
			setFilteredCrossStoreReconciliation,
		}),
		[
			apiRef,
			branchIdListTransformed,
			tableFilterState,
			tableFilterDispatch,
			reconciliationExportOptions,
			reconciliationExportDispatch,
			filteredCrossStoreReconciliation,
			setFilteredCrossStoreReconciliation,
		]
	);

	return <CrossStoreReconciliationContext.Provider value={value}>{children}</CrossStoreReconciliationContext.Provider>;
};

function useCrossStoreReconciliationContextData() {
	const context = useContext(CrossStoreReconciliationContext);
	if (!context) {
		throw new Error("useCrossStoreReconciliationContextData must be used within a CrossStoreReconciliationContextProvider");
	}
	return context;
}

export { CrossStoreReconciliationContextProvider, useCrossStoreReconciliationContextData };
